/* eslint-disable max-lines */
import { action, observable, computed } from 'mobx';
import moment from 'moment';

import BroadcasterService from 'services/BroadcasterService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import CookieStorageService from 'services/CookieStorageService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  CLOSE_PRIVATE_ROOM_DRAWER,
  FAVORITE_STATUS_RESPONSE,
  FRIEND_REQUESTS_RESPONSE,
  FRIEND_REQUEST_HANDLED,
  FRIEND_STATUS_RESPONSE,
  GET_PERFORMER_TIPPING_SESSION,
  HIDE_CHAT_EXPAND_BUTTON,
  LIVE_TOUCH_QR_CODE_TOKEN,
  SET_LOADING_WEBSERVER_TIME_REQUEST,
  SET_PAUSED_REMAINING_TIME,
  SET_PERFORMER_GOAL_MESSAGE,
  STREAM_RESUME_SUCCESS,
  UI_VARIABLES_RESPONSE,
  USER_OFFLINE_NOTIFICATION_RECEIVED,
  NEEDS_ID_MESSAGE_RECEIVED,
} from 'services/MessagingService/EventNames';
import FirebaseStore from 'stores/FirebaseStore';
import ModerationStore from 'stores/ModerationStore';
import TwitterStore from 'stores/TwitterStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import {
  ENUM_FAVOURITE_STATUS,
  ENUM_FRIEND_STATUS,
} from 'enum/profile';
import { FRIEND_REQUEST_ACTIONS } from 'utils/consts';
import {
  getCappedPercentageOfTotal,
  isStringHTMLTag,
} from 'utils/helpers';
import { USER_NOTIFICATION_STATE_LABELS } from 'firebase/firebaseStructure';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

class BroadcasterStore {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(CLOSE_PRIVATE_ROOM_DRAWER, this.handleDrawerClose);
    this.messagingService.on(GET_PERFORMER_TIPPING_SESSION, this._handlePerfomerGoal);
    this.messagingService.on(SET_PERFORMER_GOAL_MESSAGE, this._handlePerfomerMessage);
    this.messagingService.on(LIVE_TOUCH_QR_CODE_TOKEN, this.handleLiveTouchQrToken);
    this.messagingService.on(FRIEND_REQUESTS_RESPONSE, this.handleFriendRequestsResponse);
    this.messagingService.on(FRIEND_STATUS_RESPONSE, this.handleFriendStatus);
    this.messagingService.on(FAVORITE_STATUS_RESPONSE, this.handleFavoriteStatus);
    this.messagingService.on(UI_VARIABLES_RESPONSE, this.handleUiVariablesResponse);
    this.messagingService.on(USER_OFFLINE_NOTIFICATION_RECEIVED, this._handleUserOfflineNotificationReceived);
    this.messagingService.on(STREAM_RESUME_SUCCESS, this.handleStreamResumeSuccess);
    this.messagingService.on(FRIEND_REQUEST_HANDLED, this.updateFriendRequests);
    this.messagingService.on(HIDE_CHAT_EXPAND_BUTTON, this.toggleShowExpandChatButton);
    this.messagingService.on(SET_PAUSED_REMAINING_TIME, this.updatePausedRemainingTimer);
    this.messagingService.on(SET_LOADING_WEBSERVER_TIME_REQUEST, this.updateWebserverTimeRequestStatus);
    this.messagingService.on(NEEDS_ID_MESSAGE_RECEIVED, this.updateNeedsIdMessage);
  }

  @observable allowFriendsPrivateRoom = false;
  @observable allowSpecificGenderPrivateRoom = false;
  @observable specificGenderPrivateRoom = 'male';
  @observable usersInPrivateRoom = { [UserStore.user.username]: UserStore.user.username };

  @observable isPrivateRoomDrawerOpen = false;
  @observable goalBalance = 0;
  @observable goal = 0;
  @observable broadcasterMessage = '';
  @observable goalStatusMessage = '';
  @observable broadcasterGoal = 0;
  @observable liveTouchQRToken = '';
  @observable liveTouchLoading = true;
  @observable isFriendRequestDrawerOpen = false;
  @observable friendRequests = [];
  @observable acceptedRequests = [];
  @observable ignoredRequests = [];
  @observable firendRequestButtonLocked = false;
  @observable friendStatus = ENUM_FRIEND_STATUS.NONE;
  @observable favoriteStatus = ENUM_FAVOURITE_STATUS.NOT_FAVORITE;
  @observable uiVariables = {};
  @observable isLiveChatEnabled = CookieStorageService.get('live_chat_enabled') === 'true';
  @observable intercomLoading = false;
  @observable resumingStreamStatus = APIStates.INITIAL;
  @observable tippersDrawerOpen = false;
  @observable chatExpanded = false;
  @observable showChatExpandButton = false;
  @observable pausedRemainingTimer = null;
  @observable webserverTimeStatus = APIStates.INITIAL;
  @observable needsIdMessage = undefined;

  destructor() {
    this.messagingService.off(CLOSE_PRIVATE_ROOM_DRAWER, this.handleDrawerClose);
    this.messagingService.off(GET_PERFORMER_TIPPING_SESSION, this._handlePerfomerGoal);
    this.messagingService.off(SET_PERFORMER_GOAL_MESSAGE, this._handlePerfomerMessage);
    this.messagingService.off(LIVE_TOUCH_QR_CODE_TOKEN, this.handleLiveTouchQrToken);
    this.messagingService.off(FRIEND_REQUESTS_RESPONSE, this.handleFriendRequestsResponse);
    this.messagingService.off(FRIEND_STATUS_RESPONSE, this.handleFriendStatus);
    this.messagingService.off(FAVORITE_STATUS_RESPONSE, this.handleFavoriteStatus);
    this.messagingService.off(UI_VARIABLES_RESPONSE, this.handleUiVariablesResponse);
    this.messagingService.off(USER_OFFLINE_NOTIFICATION_RECEIVED, this._handleUserOfflineNotificationReceived);
    this.messagingService.off(STREAM_RESUME_SUCCESS, this.handleStreamResumeSuccess);
    this.messagingService.off(FRIEND_REQUEST_HANDLED, this.updateFriendRequests);
    this.messagingService.off(HIDE_CHAT_EXPAND_BUTTON, this.toggleShowExpandChatButton);
    this.messagingService.off(SET_PAUSED_REMAINING_TIME, this.updatePausedRemainingTimer);
    this.messagingService.off(SET_LOADING_WEBSERVER_TIME_REQUEST, this.updateWebserverTimeRequestStatus);
    this.messagingService.off(NEEDS_ID_MESSAGE_RECEIVED, this.updateNeedsIdMessage);
  }

  @action toggleExpandChat = () => {
    this.chatExpanded = !this.chatExpanded;
  };

  @action toggleShowExpandChatButton = () => {
    this.showChatExpandButton = !this.showChatExpandButton;
  };

  @action resetChatSize = () => {
    this.chatExpanded = false;
  };

  @action toggleTippersDrawer = (flag) => {
    this.tippersDrawerOpen = flag;
  };

  @action setIntercomLoading = (value) => {
    this.intercomLoading = value;
  };

  @action setLiveChatEnabled = (value) => {
    this.isLiveChatEnabled = value;
  };

  @action clearState = () => {
    this.allowFriendsPrivateRoom = false;
    this.allowSpecificGenderPrivateRoom = false;
    this.specificGenderPrivateRoom = 'male';
    this.usersInPrivateRoom = { [UserStore.user.username]: UserStore.user.username };
    this.isPrivateRoomDrawerOpen = false;
    this.goalBalance = 0;
    this.goal = 0;
    this.broadcasterMessage = '';
    this.goalStatusMessage = '';
    this.broadcasterGoal = 0;
    this.liveTouchQRToken = '';
    this.isFriendRequestDrawerOpen = false;
    this.friendRequests = [];
    this.acceptedRequests = [];
    this.ignoredRequests = [];
    this.firendRequestButtonLocked = false;
    this.friendStatus = ENUM_FRIEND_STATUS.NONE;
    this.favoriteStatus = ENUM_FAVOURITE_STATUS.NOT_FAVORITE;
    this.uiVariables = {};
    this.isLiveChatEnabled = CookieStorageService.get('live_chat_enabled') === 'true';
    this.intercomLoading = false;
    this.needsIdMessage = undefined;
  };

  @action handleFriendRequestNotification = (notification) => {
    const { a: type, i: name } = notification;
    const { user } = UserStore;

    if (name?.toLowerCase() === user.username?.toLowerCase()) {
      return;
    }

    switch (type) {
      case 'frR':
        this.friendRequests = [...this.friendRequests, name];
        break;
      case 'frC':
        this.friendRequests = this.friendRequests.filter((req) => req?.toLowerCase() !== name?.toLowerCase());
        break;
      default:
        break;
    }
  };

  getPausedRemainingTime = (end) => {
    BroadcasterService.calculateExactRemainingTime(end);
  };

  @action updatePausedRemainingTimer = (timer) => {
    this.pausedRemainingTimer = timer;
  };

  @action updateWebserverTimeRequestStatus = (status) => {
    this.webserverTimeStatus = status;
  };

  @action updateFriendRequests = (data) => {
    const { friendUsername, operation } = data;

    switch (operation) {
      case FRIEND_REQUEST_ACTIONS.ACCEPT:
        this.friendRequests = this.friendRequests.filter(
          (req) => req?.toLowerCase() !== friendUsername?.toLowerCase()
        );
        this.acceptedRequests = this.acceptedRequests.filter(
          (req) => req?.toLowerCase() !== friendUsername?.toLowerCase()
        );
        break;

      case FRIEND_REQUEST_ACTIONS.IGNORE:
        this.friendRequests = this.friendRequests.filter(
          (req) => req?.toLowerCase() !== friendUsername?.toLowerCase()
        );
        this.ignoredRequests = this.ignoredRequests.filter(
          (req) => req?.toLowerCase() !== friendUsername?.toLowerCase()
        );
        break;
      case FRIEND_REQUEST_ACTIONS.ACCEPT_ALL:
      case FRIEND_REQUEST_ACTIONS.IGNORE_ALL:
        this.friendRequests = [];
        this.acceptedRequests = [];
        this.ignoredRequests = [];
        break;
      default:
        break;
    }

    this.firendRequestButtonLocked = false;
  };

  @action _handleUserOfflineNotificationReceived = (notification) => {
    const type = notification?.a;
    switch (type) {
      case USER_NOTIFICATION_STATE_LABELS.FRIEND_REQUEST_RECEIVED:
      case USER_NOTIFICATION_STATE_LABELS.FRIEND_REQUEST_CANCELED:
        this.handleFriendRequestNotification(notification);
        break;
      case USER_NOTIFICATION_STATE_LABELS.TWITTER_CONNECT:
        if (notification?.st === USER_NOTIFICATION_STATE_LABELS.TWITTER_CONNECT_SUCCESS) {
          TwitterStore.setAuthenticated(true);
        }
        break;
      default:
        break;
    }
  };

  /**
   * Method for opening private room drawer
   */
  @action handleDrawerOpen = () => {
    ModerationStore.closeModerationPopper();
    this.isPrivateRoomDrawerOpen = true;
  };

  /**
   * Method for closing private room drawer
   */
  @action handleDrawerClose = () => {
    this.isPrivateRoomDrawerOpen = false;
  };

  /**
   * Method for handling goal and goalBalance
   */
  @action _handlePerfomerGoal = (data) => {
    this.goal = parseInt(data.goal, 10);
    this.goalBalance = parseInt(data.goalbalance, 10);
    this.goalStatusMessage = decodeURI(data.message);
  };

  /**
   * Method for handling goalBalance change
   */
  @action setBroadcasterGoal = (value) => {
    const limit = 99999;
    if (value <= limit) {
      this.broadcasterGoal = value;
    }
  };

  /**
   * Method for updating broadcaster message status
   */
  @action setBroadcasterMessage = (value) => {
    if (!isStringHTMLTag(value)) {
      this.broadcasterMessage = value;
    }
  };

  /**
   * Method for updating message and goal that comes from API
   */
  @action _handlePerfomerMessage = (data) => {
    this.broadcasterGoal = parseInt(data.goal, 10) || 0;
    this.broadcasterMessage = decodeURIComponent(data.message);
  };

  /**
   * Method for handling friend request drawer
   */
  @action handleFriendRequestDrawer = (flag) => {
    this.isFriendRequestDrawerOpen = flag;
  };

  /**
   * Method for handling friend requests response
   */
  @action handleFriendRequestsResponse = (data) => {
    this.friendRequests = data;
    this.acceptedRequests = [];
    this.ignoredRequests = [];
    this.firendRequestButtonLocked = false;
  };

  /**
   * Method for toggling checkbox for friends in private room
   */
  @action toggleFriendsPrivateRoomValue = () => {
    this.allowFriendsPrivateRoom = !this.allowFriendsPrivateRoom;
  };

  /**
   * Method for toggling checkbox for gender in private room
   */
  @action toggleGenderPrivateRoomValue = () => {
    this.allowSpecificGenderPrivateRoom = !this.allowSpecificGenderPrivateRoom;
  };

  /**
   * Method for changing gender in the private room drawer
   * @param {string} value - gender value
   */
  @action handleGenderPrivateRoomPassword = (value) => {
    this.specificGenderPrivateRoom = value;
  };

  /**
   * Method for toggling invited/not invited users in private room
   * @param {string} username - targeted username
   */
  @action toggleInvitedUsers = (username) => {
    if (this.usersInPrivateRoom[username]) {
      const deepCopy = Object.assign({}, this.usersInPrivateRoom);
      delete deepCopy[username];
      this.usersInPrivateRoom = deepCopy;
    } else {
      this.usersInPrivateRoom = { ...this.usersInPrivateRoom, [username]: username };
    }
  };

  /**
   * Method for toggling invited/not invited users in private room
   * @param {string} username - targeted username
   * @param {string} accessHash - targeted user accessHash
   * @param {string} roomName - current roomName
   * @param {bool} allowFriends - allowing friends to join private room
   * @param {string} password - private room password
   * @param {bool} privateMode - is private or public room
   * @param {string} usersString - concated users string for private room
   * @param {bool} chatless - chatless
   * @param {bool} listInDirectory - listed in directory
   * @param {string} autoAllowSex - gender that can join private room
   */
  @action enablePrivateRoom = ({
    username,
    accessHash,
    roomName,
    allowFriends,
    password,
    privateMode,
    usersString,
    chatless,
    listInDirectory,
    autoAllowSex,
    isAlreadyPrivateRoom,
  }) => {
    const postData = {
      username,
      accessHash,
      roomName,
      listInDirectory,
      allowFriends,
      password,
      privateMode,
      usersString,
      chatless,
      isAlreadyPrivateRoom,
    };
    if (autoAllowSex !== '') {
      postData.autoAllowSex = autoAllowSex;
    }
    BroadcasterService.handlePrivateRoom(postData);

    if (!privateMode) {
      this.usersInPrivateRoom = { [username]: username };
    }
  };

  /**
   * Method for setting qr token
   * @param {string} token - token value
   */
  @action handleLiveTouchQrToken = (token) => {
    this.liveTouchQRToken = token;
    this.liveTouchLoading = false;
  };

  /**
   * Method for toggling live touch on and off
   * @param {object} data - data for sending to server
   */
  handleLiveTouchConnect = (data) => {
    this.liveTouchLoading = true;
    BroadcasterService.handleLiveTouchConnect(data);
  };

  /**
   * Method for getting friend requests
   */
  getFriendRequests = () => {
    BroadcasterService.getFriendRequests();
  };

  /**
   * Method for changing gender in the private room drawer
   * @param {string} value - gender value
   */
  @action handleFriendRequest = (friendUsername, operation) => {
    this.firendRequestButtonLocked = true;
    BroadcasterService.handleFriendRequest({
      friendUsername,
      operation,
    });
    if (operation === FRIEND_REQUEST_ACTIONS.ACCEPT) {
      this.acceptedRequests.push(friendUsername);
    } else if (operation === FRIEND_REQUEST_ACTIONS.IGNORE) {
      this.ignoredRequests.push(friendUsername);
    } else if (operation === FRIEND_REQUEST_ACTIONS.ACCEPT_ALL) {
      this.acceptedRequests = this.friendRequests;
    } else if (operation === FRIEND_REQUEST_ACTIONS.IGNORE_ALL) {
      this.ignoredRequests = this.friendRequests;
    }
  };

  /* Method for getting friend status with viewer
   * @param {string} username
   * @param {string} viewerName
   */
  getPerformerFriendsStatus = (username, viewerName) => {
    BroadcasterService.getPerformerFriendsStatus(username, viewerName);
  };

  /**
   * Method for getting favorite status with viewer
   * @param {string} username
   * @param {string} viewerName
   */
  getPerformerFavoriteStatus = (username, viewerName) => {
    BroadcasterService.getPerformerFavoriteStatus(username, viewerName);
  };

  /**
   * Method for setting friend status with viewer
   * @param {string} data response
   */
  @action handleFriendStatus = (data) => {
    this.friendStatus = data.status;
  };

  /**
   * Method for setting favorite status with viewer
   * @param {string} data response
   */
  @action handleFavoriteStatus = (data) => {
    this.favoriteStatus = data.status;
  };

  @action handleStreamResumeSuccess = () => {
    this.resumingStreamStatus = APIStates.LOADED;
  };

  /**
   * Shapes the goal balance
   * @returns {number} - returns tipped tokens
   */
  @computed get tokensTipped() {
    return this.goalBalance ? parseInt(this.goalBalance, 10) : 0;
  }

  /**
   * Shapes the broadcaster goal
   * @returns {number} - returns goal
   */
  @computed get tokenGoal() {
    return this.goal ? parseInt(this.goal, 10) : 0;
  }

  /**
   * Shapes the percentage complete percentage and calculates how much left
   * @returns {number} - returns percetange left
   */
  @computed get percentageLeft() {
    return 100 - this.percentageComplete;
  }

  /**
   * Checks if there is broadcaster goal
   * @returns {bool}
   */
  @computed get hasTokenGoal() {
    return this.tokenGoal !== 0;
  }

  /**
   * Shapes the percentage complete percentage
   * @returns {number} - returns complete percentage
   */
  @computed get percentageComplete() {
    return getCappedPercentageOfTotal(this.tokenGoal, this.tokensTipped) || 0;
  }

  /**
   * Checks if goal limit is exceeded
   * @returns {bool}
   */
  @computed get goalExceeded() {
    return this.tokensTipped >= this.tokenGoal;
  }

  /**
   * Checks if goal is almost reached
   * @returns {bool}
   */
  @computed get goalAlmostReached() {
    return this.percentageLeft <= 10 && this.percentageLeft > 0;
  }

  /**
   * Shapes the raw data for ready to display props
   * @returns {string} - string of concated users to send for private room endpoint
   */
  @computed get stringFromUsersInPrivateRoom() {
    return Object.keys(this.usersInPrivateRoom).join(',');
  }

  /**
   * Shapes the raw data for ready to display props
   * @returns {Array} - array of actual users that can be invited in private room
   */
  @computed get privateRoomUserList() {
    return FirebaseStore.sortedUsers.filter((user) => UserStore.user.username !== user.originalUsername);
  }

  /**
   * Shapes source of image for QR code
   * @returns {string} - src for image
   */
  @computed get QRCodeSrc() {
    // eslint-disable-next-line max-len
    return `${BASE_URL}/qrGenerator?width=150&height=150&r2=52&g2=191&b2=73&r1=255&g1=255&b1=255&info=${this.liveTouchQRToken}`;
  }

  /**
   * Checks if friend request is accepted
   * @returns {Boolean}
   */
  isAccepted = (username) => computed(() => this.acceptedRequests.includes(username)).get();

  /**
   * Checks if friend request is ignored
   * @returns {Boolean}
   */
  isIgnored = (username) => computed(() => this.ignoredRequests.includes(username)).get();

  @computed get friendRequestsAvailable() {
    return this.friendRequests.length > 0;
  }

  /**
   * Method for resuming broadcast
   * @param {string} username
   * @param {string} accessHash
   */
  @action resumeBroadcast = (username, accessHash) => {
    this.resumingStreamStatus = APIStates.PENDING;
    BroadcasterService.resumeBroadcast(username, accessHash);
  };

  /**
   * Method for getting ui variables
   */
  getUiVariables = () => {
    BroadcasterService.getUiVariables();
  };

  /**
   * Method for setting ui variables
   */
  @action handleUiVariablesResponse = (data) => {
    this.uiVariables = data;
  };

  @computed get isStreamResuming() {
    return this.resumingStreamStatus === APIStates.PENDING;
  }

  @computed get formatedPausedTimer() {
    const date = new Date(Date.UTC(0, 0, 0, 0, 0, 0, this.pausedRemainingTimer));
    const time = `0${date.getUTCMinutes()}:${date.getUTCSeconds()}`;

    return moment(time, 'HH:mm').format('HH:mm');
  }

  isInvitedToPrivateRoom = (username) => computed(() => Boolean(this.usersInPrivateRoom[username])).get();

  getNeedsIdMessage = () => {
    BroadcasterService.getNeedsIdMessage(UserStore.user);
  };

  @action updateNeedsIdMessage = (data) => {
    this.needsIdMessage = data.needsIdMessage;
  };
}

export default new BroadcasterStore();
